<template>
  <section class="mb-8">
    <div class="mb-8">
      <h3
        id="article-title-required"
        class="font-roboto text-xl font-bold mb-2">
        Article title
      </h3>
      <AddFileCard
        v-if="!hasTitle"
        anchor-link="/submission/article-info"
        scroll-to-id="articleTitleLabel"
        data-testid="add-article-title-card"
      />
      <p
        v-else-if="hasTitle"
        id="articleTitleText"
        class="data-hj-suppress break-words bg-faint-grey rounded-lg p-4 mb-2"
        data-testid="test-article-title-final-review"
        v-html="titleHtml">
      </p>
      <InlineWarningMessage
        v-if="errorTitleMessage"
        errorTestId="article-title-error"
        :errorMessage="errorTitleMessage"
      />
    </div>

    <div class="mb-8">
      <h3
        id="article-abstract-required"
        class="font-roboto text-xl font-bold mb-2">
        Article abstract
      </h3>
      <AddFileCard
        v-if="!hasAbstract"
        anchor-link="/submission/article-info"
        scroll-to-id="articleAbstractLabel"
        data-testid="add-article-abstract-card"
      />
      <p
        v-else-if="hasAbstract"
        id="articleAbstractText"
        class="data-hj-suppress break-words bg-faint-grey rounded-lg p-4 mb-2"
        data-testid="test-article-abstract-final-review"
        v-html="abstractHtml">
      </p>
      <InlineWarningMessage
        v-if="errorAbstractMessage"
        errorTestId="article-abstract-error"
        :errorMessage="errorAbstractMessage" />
    </div>

    <h3
      class="font-roboto text-xl font-bold mb-2"
      id="ccdcd-required">
      Small molecule single crystal data
    </h3>
    <AddFileCard
      v-if="crystalDataProvided && !ccdcNumbers"
      anchor-link="/submission/article-info"
      scroll-to-id="ccdcDepositionNumbersTitle"
      data-testid="add-article-crystal-data-content"
    />
    <div v-if="crystalDataProvided">
      <div v-if="ccdcNumbers">
        <div class="bg-faint-grey rounded-lg p-4 mt-4">
          <p
            id="ccdcNumberText"
            data-testid="test-ccddNumbers-final-review"
            class="data-hj-suppress break-words"
          >
            {{ showFullContent ? ccdcNumbers : truncateByCharacters(ccdcNumbers, 100) }}
          </p>
          <ButtonComponent
            v-if="shouldShowToggle"
            :label="showFullContent ? 'View less' : 'View more'"
            variant="tertiary"
            @customClick="toggleContent"
            data-testid="show-full-content-toggle"
          />
        </div>
      </div>
      <InlineWarningMessage
        v-if="errorCCDC"
        :errorMessage="errorCCDC"
        errorTestId="ccdc-number"
        class="mt-2"/>
    </div>
    <div
      v-else
      class="bg-faint-grey rounded-md p-4 mb-4"
    >
      {{ crystalDataProvided ? '':'Not provided (optional)' }}
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { StringToHtml } from '../../utils/HtmlStringConverter'
import { useField } from 'vee-validate'
import { useStore } from 'vuex'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import { SubmissionFile, SubmissionFileType } from '@/types'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import AddFileCard from '../common/AddFileCard.vue'
import limits from '../../constants/limits'
import truncateByCharacters from '@/utils/truncateByCharacters'

const showFullContent = ref(false)
const store = useStore()
const title = store.state.activeSubmission.title
const abstract = store.state.activeSubmission.abstract
const titleHtml = StringToHtml(title)
const abstractHtml = StringToHtml(abstract)

const hasTitle = computed(() => title && title.trim())
const hasAbstract = computed(() => abstract && abstract.trim())

const ccdcInput = ref(store.state.activeSubmission.ccdcNumbers || '')
const ccdcNumbers = computed(() => {
  return store.state.activeSubmission.ccdcNumbers
})
const crystalDataProvided = computed((): boolean => {
  return store.state.activeSubmission.files.some((file: SubmissionFile) => file.type === SubmissionFileType.CrystalData)
})
const {
  errorMessage: errorTitleMessage,
  validate: validateTitle,
} = useField(
  'input-article-title',
  `article-title-required|max-words:${limits.manuscript.words.title},title`,
  { initialValue: title },
)

const {
  errorMessage: errorAbstractMessage,
  validate: validateAbstract,
} = useField(
  'input-article-abstract',
  `article-abstract-required|max-words:${limits.manuscript.words.abstract},abstract`,
  { initialValue: abstract },
)

const {
  errorMessage: errorCCDC,
  validate: validateCCDC,
} = useField('input-ccdc', `ccdc-deposition-number-required|max-characters-length:${limits.CCDC.characters.data}:Your CCDC numbers must not exceed`, {
  initialValue: ccdcInput.value,
})

watch(() => store.state.submissionAttemptCounter, () => {
  validateTitle()
  validateAbstract()
  validateCCDC()
})

function toggleContent() {
  showFullContent.value = !showFullContent.value
}

const shouldShowToggle = computed(() => {
  return ccdcNumbers.value.length > 100
})
</script>
